import { FunctionComponent, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventIcon from '@mui/icons-material/Event';
import LocationSearchingIcon from '@mui/icons-material/LocationSearching';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import ScheduleAppointmentModal from '../../modals/ScheduleAppointmentModal/ScheduleAppointmentModal';
import { claimsApi, ApiError } from '../../../services/api';
import RateProviderModal from '../../modals/RateProviderModal/RateProviderModal';
import styles from './ClaimsDetailScreen.module.css';

interface ClaimDetail {
  id: string;
  claimNumber: string;
  date: string;
  provider: string;
  providerImage?: string;
  service: string;
  serviceType: string;
  status: 'approved' | 'pending' | 'denied' | 'processing';
  totalAmount: number;
  insurancePaid: number;
  youOwe: number;
  type: 'medical' | 'dental' | 'vision' | 'pharmacy';
  servicesProvided: string[];
  diagnosis?: string;
  notes?: string;
  documents?: {
    id: string;
    name: string;
    type: string;
    url: string;
  }[];
  billing?: {
    originalCharge: number;
    negotiatedRate: number;
    deductibleApplied: number;
    copay: number;
    coinsurance: number;
    outOfPocketMax: boolean;
  };
}

const ClaimsDetailScreen: FunctionComponent = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [claimDetail, setClaimDetail] = useState<ClaimDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showRateModal, setShowRateModal] = useState(false);

  useEffect(() => {
    fetchClaimDetail();
  }, [id]);

  const fetchClaimDetail = async () => {
    if (!id) return;
    
    setIsLoading(true);
    try {
      const result = await claimsApi.getById(id);
      setClaimDetail(result);
    } catch (error) {
      console.error('Error fetching claim detail:', error);
      if (error instanceof ApiError) {
        console.error('API Error:', error.message);
      }
      // For demo purposes, use mock data if API fails
      setClaimDetail({
        id: id || 'claim-1',
        claimNumber: 'CLM2024001',
        date: '2024-11-15',
        provider: 'Dr. Olivia Bennett',
        providerImage: '/images/provider-placeholder.svg',
        service: 'General Consultation',
        serviceType: 'Routine Checkup',
        status: 'approved',
        totalAmount: 350,
        insurancePaid: 280,
        youOwe: 70,
        type: 'medical',
        servicesProvided: [
          'Physical Examination',
          'Blood Pressure Check',
          'Heart Rate Monitoring',
          'Respiratory Assessment'
        ],
        diagnosis: 'Annual wellness visit - no concerns',
        notes: 'Patient in good health. Recommended to continue current exercise routine.',
        documents: [
          { id: 'doc-1', name: 'Claim Form.pdf', type: 'pdf', url: '#' },
          { id: 'doc-2', name: 'EOB.pdf', type: 'pdf', url: '#' }
        ],
        billing: {
          originalCharge: 450,
          negotiatedRate: 350,
          deductibleApplied: 50,
          copay: 20,
          coinsurance: 0,
          outOfPocketMax: false
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'denied': return '#ef4444';
      case 'processing': return '#3b82f6';
      default: return '#666';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircleIcon />;
      case 'pending': return <EventIcon />;
      case 'denied': return <ReportProblemIcon />;
      case 'processing': return <EventIcon />;
      default: return null;
    }
  };

  if (isLoading || !claimDetail) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner} />
        <p>Loading claim details...</p>
      </div>
    );
  }

  return (
    <div className={styles.claimsDetailScreen}>
      <div className={styles.depth0Frame0}>
        <div className={styles.depth1Frame0}>
          {/* Header */}
          <div className={styles.depth2Frame0}>
            <button className={styles.backButton} onClick={() => navigate(-1)}>
              <ArrowBackIcon />
            </button>
            <div className={styles.depth3Frame1}>
              <b className={styles.claimDetails}>Claim Details</b>
            </div>
            <div className={styles.headerActions}>
              <button className={styles.iconButton} onClick={() => window.print()}>
                <DownloadIcon />
              </button>
              <button className={styles.iconButton} onClick={() => navigator.share({ title: 'Claim Details', text: `Claim ${claimDetail.claimNumber}` })}>
                <ShareIcon />
              </button>
            </div>
          </div>

          {/* Claim Number and Status */}
          <div className={styles.claimHeader}>
            <div className={styles.claimNumberSection}>
              <span className={styles.claimNumberLabel}>Claim #</span>
              <span className={styles.claimNumber}>{claimDetail.claimNumber}</span>
            </div>
            <div className={styles.statusBadge} style={{ backgroundColor: `${getStatusColor(claimDetail.status || 'pending')}20`, color: getStatusColor(claimDetail.status || 'pending') }}>
              {getStatusIcon(claimDetail.status || 'pending')}
              <span>{(claimDetail.status || 'pending').charAt(0).toUpperCase() + (claimDetail.status || 'pending').slice(1)}</span>
            </div>
          </div>

          {/* Claim Summary */}
          <div className={styles.depth2Frame1}>
            <b className={styles.claimSummary}>Claim Summary</b>
          </div>

          {/* Provider Info */}
          <div className={styles.depth2Frame2}>
            <img className={styles.depth3Frame2} alt="" src={claimDetail.providerImage || "/images/provider-placeholder.svg"} />
            <div className={styles.depth3Frame11}>
              <div className={styles.depth4Frame0}>
                <div className={styles.drOliviaBennett}>{claimDetail.provider}</div>
              </div>
              <div className={styles.depth4Frame1}>
                <div className={styles.div}>{formatDate(claimDetail.date)}</div>
              </div>
            </div>
          </div>

          {/* Service Info */}
          <div className={styles.depth2Frame3}>
            <div className={styles.iconWrapper}>
              <LocalHospitalIcon />
            </div>
            <div className={styles.depth3Frame11}>
              <div className={styles.depth4Frame0}>
                <div className={styles.drOliviaBennett}>{claimDetail.service}</div>
              </div>
              <div className={styles.depth4Frame11}>
                <div className={styles.div}>{claimDetail.serviceType}</div>
              </div>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className={styles.costBreakdownSection}>
            <b className={styles.sectionTitle}>Cost Breakdown</b>
            <div className={styles.costBreakdown}>
              <div className={styles.costRow}>
                <span className={styles.costLabel}>Original Charge</span>
                <span className={styles.costValue}>{formatCurrency(claimDetail.billing?.originalCharge || claimDetail.totalAmount)}</span>
              </div>
              <div className={styles.costRow}>
                <span className={styles.costLabel}>Negotiated Rate</span>
                <span className={styles.costValue} style={{ color: '#10b981' }}>{formatCurrency(claimDetail.totalAmount)}</span>
              </div>
              <div className={styles.costDivider} />
              <div className={styles.costRow}>
                <span className={styles.costLabel}>Insurance Paid</span>
                <span className={styles.costValue}>{formatCurrency(claimDetail.insurancePaid)}</span>
              </div>
              <div className={styles.costRow}>
                <span className={styles.costLabel}>Deductible Applied</span>
                <span className={styles.costValue}>{formatCurrency(claimDetail.billing?.deductibleApplied || 0)}</span>
              </div>
              <div className={styles.costRow}>
                <span className={styles.costLabel}>Copay</span>
                <span className={styles.costValue}>{formatCurrency(claimDetail.billing?.copay || 0)}</span>
              </div>
              <div className={styles.costDivider} />
              <div className={styles.costRow + ' ' + styles.totalRow}>
                <span className={styles.costLabel}><b>You Owe</b></span>
                <span className={styles.costValue}><b>{formatCurrency(claimDetail.youOwe)}</b></span>
              </div>
            </div>
            {claimDetail.billing?.outOfPocketMax && (
              <div className={styles.oopMaxNotice}>
                ✅ You've reached your out-of-pocket maximum
              </div>
            )}
          </div>

          {/* Services Provided */}
          <div className={styles.depth2Frame1}>
            <b className={styles.claimSummary}>Services Provided</b>
          </div>
          {claimDetail.servicesProvided.map((service, index) => (
            <div key={index} className={styles.depth2Frame6}>
              <div className={styles.depth3Frame03}>
                <div className={styles.physicalExamination}>{service}</div>
              </div>
            </div>
          ))}

          {/* Diagnosis & Notes */}
          {(claimDetail.diagnosis || claimDetail.notes) && (
            <>
              <div className={styles.depth2Frame1}>
                <b className={styles.claimSummary}>Diagnosis & Notes</b>
              </div>
              <div className={styles.notesSection}>
                {claimDetail.diagnosis && (
                  <div className={styles.diagnosisRow}>
                    <span className={styles.diagnosisLabel}>Diagnosis:</span>
                    <span className={styles.diagnosisValue}>{claimDetail.diagnosis}</span>
                  </div>
                )}
                {claimDetail.notes && (
                  <div className={styles.notesRow}>
                    <span className={styles.notesLabel}>Provider Notes:</span>
                    <span className={styles.notesValue}>{claimDetail.notes}</span>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Documents */}
          {claimDetail.documents && claimDetail.documents.length > 0 && (
            <>
              <div className={styles.depth2Frame1}>
                <b className={styles.claimSummary}>Documents</b>
              </div>
              {claimDetail.documents.map((doc) => (
                <div key={doc.id} className={styles.documentRow} onClick={() => window.open(doc.url, '_blank')}>
                  <div className={styles.documentInfo}>
                    <DownloadIcon className={styles.documentIcon} />
                    <span className={styles.documentName}>{doc.name}</span>
                  </div>
                  <ChevronRightIcon className={styles.chevronIcon} />
                </div>
              ))}
            </>
          )}

          {/* Follow-up Actions */}
          <div className={styles.depth2Frame1}>
            <b className={styles.claimSummary}>Follow-up Actions</b>
          </div>
          
          <div className={styles.depth2Frame11} onClick={() => setShowScheduleModal(true)}>
            <div className={styles.depth3Frame07}>
              <div className={styles.actionIcon}>
                <CalendarMonthIcon />
              </div>
              <div className={styles.depth3Frame03}>
                <div className={styles.physicalExamination}>Schedule Follow-up</div>
              </div>
            </div>
            <ChevronRightIcon className={styles.chevronIcon} />
          </div>

          <div className={styles.depth2Frame11} onClick={() => navigate(`/search?type=providers&service=${encodeURIComponent(claimDetail.service)}`)}>
            <div className={styles.depth3Frame07}>
              <div className={styles.actionIcon}>
                <LocationSearchingIcon />
              </div>
              <div className={styles.depth3Frame03}>
                <div className={styles.physicalExamination}>Find Similar Services Nearby</div>
              </div>
            </div>
            <ChevronRightIcon className={styles.chevronIcon} />
          </div>

          <div className={styles.depth2Frame11} onClick={() => setShowRateModal(true)}>
            <div className={styles.depth3Frame07}>
              <div className={styles.actionIcon}>
                <StarBorderIcon />
              </div>
              <div className={styles.depth3Frame03}>
                <div className={styles.physicalExamination}>Rate Your Visit</div>
              </div>
            </div>
            <ChevronRightIcon className={styles.chevronIcon} />
          </div>

          <div className={styles.depth2Frame11} onClick={() => navigate(`/claims/${claimDetail.id}/dispute`)}>
            <div className={styles.depth3Frame07}>
              <div className={styles.actionIcon}>
                <ReportProblemIcon />
              </div>
              <div className={styles.depth3Frame03}>
                <div className={styles.physicalExamination}>Dispute Claim</div>
              </div>
            </div>
            <ChevronRightIcon className={styles.chevronIcon} />
          </div>
        </div>
      </div>
      
      {/* Schedule Appointment Modal */}
      <ScheduleAppointmentModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        providerId="provider-1" // In a real app, this would come from the claim data
        providerName={claimDetail.provider}
        providerImage={claimDetail.providerImage}
        service={claimDetail.service}
      />
      
      {/* Rate Provider Modal */}
      <RateProviderModal
        isOpen={showRateModal}
        onClose={() => setShowRateModal(false)}
        providerId="provider-1" // In a real app, this would come from the claim data
        providerName={claimDetail.provider}
        providerImage={claimDetail.providerImage}
        visitDate={claimDetail.date}
        service={claimDetail.service}
      />
    </div>
  );
};

export default ClaimsDetailScreen;